import React, { useRef, useState } from "react";
import {
  Upload,
  FileSpreadsheet,
  Download,
  History,
  Loader2,
  AlertCircle,
} from "lucide-react";
import CommonButton from "../../../components/CommonButton";
import {
  useInventoryUploadMutation,
  useLazyInventoryExportQuery,
} from "../../../redux/actions/Admin/dashboardAdmin";
import { useNavigate } from "react-router-dom";
import {
  ADMIN_INVENTORY_DOWNLOAD,
  INVENTORY_EXPORT,
} from "../../../config/apiUrlsAdmin";

const AdminInventory: React.FC = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const [inventoryUpload, { isLoading }] = useInventoryUploadMutation();
  const [triggerExport, { isLoading: isExporting }] =
    useLazyInventoryExportQuery();

  const [uploadedData, setUploadedData] = useState<{
    batchId: string;
    fileName: string;
    file: File | null;
  } | null>(null);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      setUploadError(null); // Reset visibility error banners when a fresh file is chosen
    }
  };

  const handleDeleteFile = () => {
    setSelectedFile(null);
    setUploadError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleUploadInventory = async () => {
    if (!selectedFile) return;
    setUploadError(null);

    try {
      const response = await inventoryUpload({ file: selectedFile }).unwrap();
      // ... success tracking paths ...
      navigate("/admin/product/inventoryupload/inventoryalluploadscreen");
    } catch (error: any) {
      console.error("Component Caught Upload Error:", error);

      // Unpacks error payload whether it arrives flattened or nested inside error.data
      const serverPayload = error?.data || error;
      const backendMessage = serverPayload?.Message || serverPayload?.message;

      if (backendMessage && typeof backendMessage === "string") {
        setUploadError(backendMessage);
      } else {
        setUploadError(
          "Upload failed. Please make sure all required fields are filled in and try again."
        );
      }
    }
  };

  const handleDownloadMasterInventory = async () => {
    try {
      const response = await fetch(ADMIN_INVENTORY_DOWNLOAD, {
        method: "GET",
                 credentials: "include",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`, // remove if no auth
        },
      });

      if (!response.ok) {
        throw new Error("Failed to download inventory");
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `Petland_Inventory_Template${new Date()
        .toISOString()
        .slice(0, 10)}.xlsx`;

      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Download failed:", error);
    }
  };

  return (
    <div className="min-h-screen bg-white px-5 py-6">
      <div className="max-w-6xl mx-auto">
        {/* ACTIONS TOP BANNER RUNNER */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between pb-6 border-b border-gray-100 mb-8 gap-4">
          <div className="mb-2 lg:mb-0 text-left">
            <h1 className="text-2xl sm:text-3xl font-black text-logoHeading">
              Inventory Upload
            </h1>
            <p className="text-sm text-iconColor mt-1">
              Upload an Excel file to update or download your product inventory.
            </p>
          </div>

          <div className="flex items-center gap-3">
            <CommonButton
              text={isExporting ? "Exporting..." : "Download Template"}
              variant="outline"
              icon={
                isExporting ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Download className="w-4 h-4" />
                )
              }
              disabled={isExporting || isLoading}
              className="border-slate-200 text-slate-700 font-semibold text-xs"
              onClick={handleDownloadMasterInventory}
            />
            <CommonButton
              text="View Upload History"
              variant="filled"
              icon={<History className="w-4 h-4" />}
              className="bg-mainBrandColor font-semibold text-xs"
              onClick={() =>
                navigate(
                  "/admin/product/inventoryupload/inventoryalluploadscreen"
                )
              }
            />
          </div>
        </div>

        {/* VISUAL COMPILATION PROCESSING ERROR ALERTS DISPLAY STRIP */}
        {uploadError && (
          <div className="mb-6 flex items-start gap-3 bg-red-50 border border-red-200 text-red-800 rounded-2xl p-4 shadow-sm animate-fade-in text-left">
            <AlertCircle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
            <div>
              <h4 className="font-bold text-sm">Upload Failed</h4>
              <p className="text-xs text-red-700 mt-0.5 leading-relaxed font-semibold">
                {uploadError}
              </p>
            </div>
          </div>
        )}

        {/* MAIN DRAG DROP CARD */}
        <div className="bg-[#f9f9f9] rounded-[2rem] border border-accent/30 shadow-lg overflow-hidden">
          <div className="grid grid-cols-1 lg:grid-cols-2">
            {/* LEFT INPUT INTERACTION COLUMN */}
            <div className="p-6 sm:p-8 lg:p-10 border-b lg:border-b-0 lg:border-r border-accent/20">
              <div className="flex items-center gap-4 mb-8 text-left">
                <div className="w-14 h-14 rounded-2xl bg-accent/20 flex items-center justify-center">
                  <FileSpreadsheet className="text-mainBrandColor w-7 h-7" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-logoHeading">
                  Upload Excel File
                  </h2>
                  <p className="text-sm text-iconColor">
                  Supported formats: .xls, .xlsx
                  </p>
                </div>
              </div>

              {!selectedFile ? (
                <div
                  onClick={() => !isLoading && fileInputRef.current?.click()}
                  className="group border-2 border-dashed border-mainBrandColor/40 rounded-[2rem] bg-accent/10 hover:bg-accent/20 transition-all duration-300 cursor-pointer p-8 sm:p-12 text-center"
                >
                  <div className="flex justify-center mb-5">
                    <div className="w-20 h-20 rounded-full bg-white shadow-md flex items-center justify-center group-hover:scale-110 transition-all duration-300">
                      <Upload className="w-10 h-10 text-mainBrandColor" />
                    </div>
                  </div>
                  <h3 className="text-xl font-bold text-logoHeading">
                  Choose an Excel File
                  </h3>
                  <p className="text-sm text-iconColor mt-2">
                  Click to choose a file from your computer.
                  </p>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".xls,.xlsx"
                    className="hidden"
                    onChange={handleFileChange}
                  />
                </div>
              ) : (
                <div className="rounded-[2rem] border border-mainBrandColor/20 bg-accent/10 p-5 sm:p-6 text-left">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-5 pb-5">
                    <div className="flex items-center gap-4">
                      <div className="w-16 h-16 rounded-2xl bg-white shadow-sm flex items-center justify-center shrink-0">
                        <FileSpreadsheet className="w-8 h-8 text-mainBrandColor" />
                      </div>
                      <div>
                        <p className="text-sm font-bold text-logoHeading">
                        Selected File
                        </p>
                        <p className="text-sm font-semibold text-logoHeading break-all mt-1">
                          {selectedFile.name}
                        </p>
                      </div>
                    </div>
                    <CommonButton
                      text="Delete"
                      variant="filled"
                      disabled={isLoading}
                      className="border border-red-200 bg-red-50 hover:bg-red-100 text-red-600 disabled:opacity-40"
                      onClick={handleDeleteFile}
                    />
                  </div>

                  <CommonButton
                    text={
                      isLoading
                        ? "Uploading..."
                        : "Upload File"
                    }
                    variant="filled"
                    disabled={isLoading}
                    className="w-full bg-mainBrandColor text-white font-bold py-3 disabled:bg-gray-300"
                    onClick={handleUploadInventory}
                  />
                </div>
              )}
            </div>

            {/* RIGHT SIDE DATA INSTRUCTIONS */}
            <div className="p-6 sm:p-8 lg:p-10 bg-white text-left">
              <h2 className="text-2xl font-black text-logoHeading mb-6">
              Before You Upload
              </h2>
              <div className="space-y-5">
                {[
                  "Upload only .xls or .xlsx files.",
                  "Make sure there are no duplicate product names.",
                  "Do not delete or rename any columns in the template.",
                  "Check the upload results and fix any errors before using the data.",
                ].map((item, index) => (
                  <div key={index} className="flex items-start gap-4">
                    <div className="w-7 h-7 rounded-full bg-mainBrandColor text-white flex items-center justify-center text-xs font-bold shrink-0 mt-0.5">
                      {index + 1}
                    </div>
                    <p className="text-sm text-iconColor leading-relaxed">
                      {item}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminInventory;
